import { CheckCircleOutlined } from "@mui/icons-material";
import { AlertProps, Button } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../../../../config";
import { ROUTE_SLUGS } from "../../../../../../config/navigation/routes";
import { useGetUser } from "../../../../../../hooks";
import { useUpdateEcfrPinMutation } from "../../../../../../hooks/mutations";
import {
  captureSentryException,
  fillRouteWithSlugs,
} from "../../../../../../utils";
import { getUserHomeOrg } from "../../../../../../utils/user";
import GenericModal from "../../../../../GenericModal/GenericModal";
import CreatePinInput from "./setup/SetupPinInput";
import {
  handleConfirmPinChange,
  handlePinChange,
  setupPinWarningText,
} from "./utils";

export const ResetPinModal = ({ isOpen }: { isOpen: boolean }) => {
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [alert, setAlert] = useState<{
    severity: AlertProps["severity"];
    message: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const updateEcfrPinMutation = useUpdateEcfrPinMutation();
  const navigate = useNavigate();
  const { data: user } = useGetUser();

  const handleReset = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    setIsLoading(true);

    const result = await updateEcfrPinMutation.mutateAsync(pin);

    if ("error" in result) {
      setAlert({
        severity: "error",
        message: "Failed to reset PIN. Try again.",
      });
      captureSentryException(result.error);
      setIsLoading(false);
      return;
    }

    setAlert({
      severity: "success",
      message: "Approval Pin Reset Successfully",
    });
    setIsLoading(false);
  };

  const handleTakeMeHome = () => {
    if (user) {
      const homeOrgId = getUserHomeOrg(user)?.id ?? "";
      const orgId =
        homeOrgId || user.organizationMemberships[0]?.organizationId;
      if (orgId) {
        navigate(
          fillRouteWithSlugs(ROUTES.DASHBOARD, {
            [ROUTE_SLUGS.ORG_ID]: orgId,
          })
        );
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isDisabled) {
      e.preventDefault();
      handleReset();
    }
  };

  const isDisabled =
    isLoading ||
    pin.length !== 4 ||
    confirmPin.length !== 4 ||
    pin !== confirmPin;

  const isSuccess = alert?.severity === "success";

  return (
    <GenericModal
      isOpen={isOpen}
      title={isSuccess ? "" : "Reset Approval Pin"}
      handleSave={handleReset}
      handleCancel={handleTakeMeHome}
      hideActions={isSuccess}
      isDisabled={isDisabled}
      isLoading={isLoading}
      cancelText="Take Me Home"
      saveText="Confirm"
      width="550px"
      {...(!isSuccess && {
        alertText: setupPinWarningText,
        alertSeverity: "warning",
        description:
          "Please set a pin that will be used for all approvals within Formly.",
      })}
    >
      {isSuccess ? (
        <div className="flex flex-col items-center justify-center gap-4 py-8">
          <CheckCircleOutlined color="success" sx={{ fontSize: 64 }} />
          <div className="text-xl font-medium">
            Approval Pin Reset Successfully
          </div>
          <Button
            variant="contained"
            color="primary"
            onClick={handleTakeMeHome}
          >
            Take Me Home
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <CreatePinInput
            pin={pin}
            confirmPin={confirmPin}
            onPinChange={(e) =>
              handlePinChange(e, setPin, confirmPin, setAlert)
            }
            onConfirmPinChange={(e) =>
              handleConfirmPinChange(e, setConfirmPin, pin, setAlert)
            }
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            alert={{ severity: alert?.severity, alertContent: alert?.message }}
          />
        </div>
      )}
    </GenericModal>
  );
};

export default ResetPinModal;
